/**
 * Static content for the landing page. No Vue here, no fetch: everything the
 * page shows is either a literal the game already ships (a render path, an id
 * the catalogue defines) or a figure imported from the server's own shared
 * protocol, so the page can never drift from what the game does.
 *
 * Copy is addressed through i18n keys, not written here — this file is the
 * shape and the wiring, `locales/en_GB.json`'s `landing.*` block is the words.
 */
import { type LayoutId, layoutById } from '@shared/islands';
import { type MatchMode, modeRules } from '@shared/modes';
import { BATTLE_SECONDS } from '@shared/protocol';
import { RANKS } from '@shared/ranks';
import { SHIP_SPECS, type ShipSpec } from '@shared/ship-spec';

import type { GameIconName } from '@/ui/kit/icons';
import type { PORTRAIT_IDS } from '@/ui/panel/art-path';

export type ShipClass = 'destroyer' | 'cruiser' | 'battleship';

export interface FleetClass {
  readonly id: ShipClass;
  readonly nameKey: string;
  readonly blurbKey: string;
}

/** One line of character per class — the copy a visitor reads above its four hulls. */
export const FLEET_CLASSES: readonly FleetClass[] = [
  { id: 'destroyer', nameKey: 'landing.fleet.class.destroyer.name', blurbKey: 'landing.fleet.class.destroyer.blurb' },
  { id: 'cruiser', nameKey: 'landing.fleet.class.cruiser.name', blurbKey: 'landing.fleet.class.cruiser.blurb' },
  { id: 'battleship', nameKey: 'landing.fleet.class.battleship.name', blurbKey: 'landing.fleet.class.battleship.blurb' },
];

export interface HullEntry {
  readonly id: string;
  readonly shipClass: ShipClass;
  readonly tier: number;
  /** The catalogue's own render, reused rather than drawn again for the landing page. */
  readonly render: string;
  readonly nameKey: string;
}

function hullEntry(id: string, shipClass: ShipClass, tier: number): HullEntry {
  return {
    id,
    shipClass,
    tier,
    render: `/assets/catalogue/${id}.webp`,
    nameKey: `shipyard.hull.${id}.name`,
  };
}

function hullTier(shipClass: ShipClass, tier: number): HullEntry {
  return hullEntry(`${shipClass}_t${tier}`, shipClass, tier);
}

/**
 * The twelve line hulls, in tier order within each class — `catalogue.data.ts`'s
 * own order — and the premium battleship after them, as `PREMIUM_HULLS` there.
 */
export const HULLS: readonly HullEntry[] = [
  hullTier('destroyer', 1),
  hullTier('destroyer', 2),
  hullTier('destroyer', 3),
  hullTier('destroyer', 4),
  hullTier('cruiser', 1),
  hullTier('cruiser', 2),
  hullTier('cruiser', 3),
  hullTier('cruiser', 4),
  hullTier('battleship', 1),
  hullTier('battleship', 2),
  hullTier('battleship', 3),
  hullTier('battleship', 4),
  hullEntry('battleship_sovereign', 'battleship', 4),
];

export interface ModeEntry {
  readonly id: MatchMode;
  readonly painting: string;
  readonly paintingJpg: string;
  readonly nameKey: string;
  readonly blurbKey: string;
  /** The victory-point cap `battle.modes.blurb.<id>` interpolates as `{cap}` (ignored by blurbs that don't use it). */
  readonly cap: number;
  /** The chart the card's back shows: the real thumbnail, `public/art/charts/<layout>.svg`. */
  readonly layout: LayoutId;
  readonly chart: string;
  /** Capture rings the mode is fought over on that chart, from the rules themselves. */
  readonly rings: number;
  /** The battle clock, whole minutes. */
  readonly minutes: number;
}

/**
 * A representative chart per mode, chosen by Arturs (life-layer.md §3 P4): the
 * thumbnail on a mode card's back is one real chart that mode is played on,
 * not an illustration of the rules.
 */
export const MODE_LAYOUT: Readonly<Record<MatchMode, LayoutId>> = {
  domination: 'archipelago',
  encounter: 'strait',
  annihilation: 'open',
  epicentre: 'atoll',
  convoy: 'sound',
};

function mode(id: MatchMode): ModeEntry {
  const rules = modeRules(id);
  const layout = MODE_LAYOUT[id];
  return {
    id,
    painting: `/art/mode-${id}.webp`,
    paintingJpg: `/art/mode-${id}.jpg`,
    nameKey: `battle.modes.name.${id}`,
    blurbKey: `battle.modes.blurb.${id}`,
    // The rules' own cap: retune a mode and this page follows.
    cap: rules.victoryPoints,
    layout,
    chart: `/art/charts/${layout}.svg`,
    rings: rules.rings(layoutById(layout)).length,
    minutes: rules.battleSeconds / 60,
  };
}

/** The five modes, in the order the sprint doc fixes. */
export const MODES: readonly ModeEntry[] = [
  mode('encounter'),
  mode('domination'),
  mode('annihilation'),
  mode('convoy'),
  mode('epicentre'),
];

/** What the hero's chips count. `hulls` is pinned to the catalogue by `landing-content.test.ts`. */
export const FIGURES = {
  hulls: 13,
  modes: MODES.length,
  ranks: RANKS.length,
  battleMinutes: BATTLE_SECONDS / 60,
} as const;

// ─── Fleet: the three classes and what each hull is worth ───────────────────

/** Metres per second to knots, for the speed a captain reads. */
const MS_TO_KNOTS = 1.944;

/**
 * What a hull item overrides of its class's stock spec: `hp` and `maxSpeed`
 * from `backend/src/game/shipyard/catalogue.data.ts` (`HULLS`, `PREMIUM_HULLS`).
 * The hulls that name neither (the tier ones and the cruiser's second) take
 * the class value. `landing-content.test.ts` reads the catalogue and fails if
 * this table drifts from it. The shared bundle does not carry the catalogue
 * (the production image builds with `@shared` only), so it is copied here.
 */
const HULL_OVERRIDES: Readonly<Record<string, { hp?: number; maxSpeed?: number }>> = {
  destroyer_t2: { hp: 15_400, maxSpeed: 18.5 },
  destroyer_t3: { hp: 17_200, maxSpeed: 20 },
  destroyer_t4: { hp: 19_600, maxSpeed: 21 },
  cruiser_t2: { hp: 31_400 },
  cruiser_t3: { hp: 37_000, maxSpeed: 15.5 },
  cruiser_t4: { hp: 45_000, maxSpeed: 16 },
  battleship_t2: { hp: 70_000, maxSpeed: 11.5 },
  battleship_t3: { hp: 82_000, maxSpeed: 12 },
  battleship_t4: { hp: 95_000, maxSpeed: 13 },
  battleship_sovereign: { hp: 88_000, maxSpeed: 14 },
};

export interface HullStats {
  hp: number;
  speedKn: number;
  /** Gun mounts on the hull. */
  mounts: number;
  /** One full salvo's shell damage with the class's stock mounting: what the GUNS bar measures. */
  salvo: number;
}

function salvoDamage(spec: ShipSpec): number {
  return spec.turrets.reduce((sum, t) => sum + t.shellDamage, 0);
}

export function hullStats(hull: HullEntry): HullStats {
  const base = SHIP_SPECS[hull.shipClass];
  const o = HULL_OVERRIDES[hull.id] ?? {};
  return {
    hp: o.hp ?? base.hp,
    speedKn: Math.round((o.maxSpeed ?? base.maxSpeed) * MS_TO_KNOTS),
    mounts: base.turrets.length,
    salvo: salvoDamage(base),
  };
}

/** The largest of each stat over every hull, so a bar reads as a share of the best ship in the game. */
export const STAT_CEILING: HullStats = HULLS.reduce<HullStats>(
  (max, h) => {
    const s = hullStats(h);
    return {
      hp: Math.max(max.hp, s.hp),
      speedKn: Math.max(max.speedKn, s.speedKn),
      mounts: Math.max(max.mounts, s.mounts),
      salvo: Math.max(max.salvo, s.salvo),
    };
  },
  { hp: 0, speedKn: 0, mounts: 0, salvo: 0 },
);

export function hullsOfClass(shipClass: ShipClass): readonly HullEntry[] {
  return HULLS.filter((h) => h.shipClass === shipClass);
}

const ROMAN = ['I', 'II', 'III', 'IV'] as const;
export function tierNumeral(tier: number): string {
  return ROMAN[tier - 1] ?? String(tier);
}

// ─── Ranks: the ladder and the captain who ages on it ───────────────────────

export type PortraitId = (typeof PORTRAIT_IDS)[number];

/** A captain's three ages: the portrait art has a junior, a senior and a flag-rank face per class. */
export type CaptainAge = 'junior' | 'senior' | 'flag';

/** First rank index of each age. The ladder is 15 rungs: five to a face (`@shared/ranks`). */
export const AGE_STARTS: Readonly<Record<CaptainAge, number>> = { junior: 0, senior: 5, flag: 10 };

export function captainAge(rankIndex: number): CaptainAge {
  if (rankIndex >= AGE_STARTS.flag) return 'flag';
  return rankIndex >= AGE_STARTS.senior ? 'senior' : 'junior';
}

/** The class whose captain the page ages: the cruiser, the balanced hull a new player is most likely to know. */
export const PORTRAIT_CLASS: ShipClass = 'cruiser';

export function portraitFor(rankIndex: number): PortraitId {
  return `${PORTRAIT_CLASS}_${captainAge(rankIndex)}`;
}

/** Ranks lit for a ladder `progress` (0..1): none at 0, all at 1. */
export function ranksReached(progress: number, total = RANKS.length): number {
  return Math.round(Math.min(1, Math.max(0, progress)) * total);
}

// ─── Navigation and the harbour ─────────────────────────────────────────────

export interface NavItem {
  /** Matches a `data-section` attribute on the section it points to. */
  readonly id: string;
  readonly labelKey: string;
  readonly icon: GameIconName;
}

/** The top bar's tabs: one painted icon per section, in page order. */
export const NAV: readonly NavItem[] = [
  { id: 'fleet', labelKey: 'landing.nav.fleet', icon: 'warship' },
  { id: 'modes', labelKey: 'landing.nav.modes', icon: 'flag' },
  { id: 'ranks', labelKey: 'landing.nav.ranks', icon: 'medal' },
  { id: 'harbour', labelKey: 'landing.nav.harbour', icon: 'anchor' },
  { id: 'notices', labelKey: 'landing.nav.notices', icon: 'bell' },
  { id: 'faq', labelKey: 'landing.nav.faq', icon: 'lighthouse' },
];

/**
 * The stations the harbour mock shows, with the id `ui/hud/HarbourHud.vue`
 * keys its icons on and the copy `stations.<id>.label/hint` already holds.
 */
export const HARBOUR_STATION_IDS = ['shipyard', 'research', 'profile', 'papers', 'shop', 'office'] as const;
export type HarbourStationId = (typeof HARBOUR_STATION_IDS)[number];

/** The icon the harbour HUD draws for each station (`HarbourHud.vue`'s own map). */
export const HARBOUR_STATION_ICON: Readonly<Record<HarbourStationId, GameIconName>> = {
  shipyard: 'turret',
  research: 'blueprint',
  profile: 'cap',
  papers: 'letter',
  shop: 'chest',
  office: 'bell',
};
